/**
 * Admin Media Buying Dashboard — TJ Command Center Phase 1.5A
 *
 * Enhanced with:
 * - Overview: CPM, Reach, Frequency, Link Clicks, Cost per Link Click,
 *   Landing Page Views, Quality Rankings
 * - Campaigns: expandable hierarchy (Campaign → Ad Set → Ad)
 * - Trends: Spend vs Leads, CPL Trend
 * - Account Health: connected accounts + sync status
 */

import { useState, useMemo, Fragment } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DateRangeFilter, DateRangeValue, PresetKey } from "@/components/DateRangeFilter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useAdCampaigns,
  useAdSets,
  useAdCreatives,
  useAdPerformanceSnapshots,
  useAdPerformanceAllLevels,
  useTriggerMetaSync,
  aggregateSnapshots,
  type AdPerformanceSnapshot,
  type AggregatedMetrics,
} from "@/hooks/useAdPerformance";
import { useToast } from "@/hooks/use-toast";
import { AccountHealthTab } from "@/components/admin/AccountHealthTab";
import { CreativesTab } from "@/components/admin/CreativesTab";
import { GeoPerformanceTab } from "@/components/admin/GeoPerformanceTab";
import { BudgetPacingCard } from "@/components/admin/BudgetPacingCard";
import { AIAdvisorTab } from "@/components/admin/AIAdvisorTab";
import {
  useSystemLeadsAttribution,
  buildSystemLeadsOverlay,
  overlaySystemLeadsOnSnapshots,
  type SystemLeadsOverlay,
} from "@/hooks/useSystemLeads";
import {
  DollarSign,
  TrendingUp,
  Users,
  Target,
  RefreshCw,
  Loader2,
  BarChart3,
  Eye,
  Radio,
  MousePointerClick,
  MonitorSmartphone,
  ChevronRight,
  ChevronDown,
  Layers,
  Brain,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

// ─── Helpers ──────────────────────────────────────────────

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US").format(Math.round(value));
}

function formatPct(value: number): string {
  return `${value.toFixed(2)}%`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatFrequency(value: number): string {
  return value.toFixed(1);
}

function getDateRange(preset: PresetKey | undefined, range: DateRangeValue | undefined) {
  if (range) {
    return {
      from: range.from.toISOString().slice(0, 10),
      to: range.to.toISOString().slice(0, 10),
    };
  }
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

/** Render a quality ranking badge */
function RankingBadge({ value, label }: { value: string | null; label: string }) {
  if (!value) return <span className="text-xs text-muted-foreground">—</span>;

  const normalized = value.toUpperCase().replace(/_/g, " ");
  let color = "text-muted-foreground bg-muted";
  if (normalized.includes("ABOVE")) color = "text-green-700 bg-green-50 border-green-200";
  else if (normalized.includes("BELOW")) color = "text-red-700 bg-red-50 border-red-200";
  else if (normalized.includes("AVERAGE")) color = "text-yellow-700 bg-yellow-50 border-yellow-200";

  const displayValue = normalized.includes("ABOVE") ? "Above Avg" : normalized.includes("BELOW") ? "Below Avg" : "Average";

  return (
    <div className="text-center">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <span className={`text-xs px-2 py-0.5 rounded border ${color}`}>{displayValue}</span>
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────

function OverviewTab({
  snapshots,
  isLoading,
}: {
  snapshots: AdPerformanceSnapshot[];
  isLoading: boolean;
}) {
  const metrics = useMemo(() => aggregateSnapshots(snapshots), [snapshots]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (snapshots.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <BarChart3 className="h-12 w-12 mb-4" />
        <p className="text-lg font-medium">No ad performance data yet</p>
        <p className="text-sm mt-1">
          Sync your Meta Ads account to see performance data here.
        </p>
      </div>
    );
  }

  const primaryKpis = [
    { label: "Total Spend", value: formatCurrency(metrics.totalSpend), icon: DollarSign, color: "text-red-600" },
    { label: "Leads", value: formatNumber(metrics.totalLeads), icon: Users, color: "text-green-600" },
    { label: "Avg CPL", value: metrics.totalLeads > 0 ? formatCurrency(metrics.avgCPL) : "—", icon: Target, color: "text-blue-600" },
    { label: "CTR", value: formatPct(metrics.avgCTR), icon: TrendingUp, color: "text-purple-600" },
    { label: "CPM", value: formatCurrency(metrics.avgCPM), icon: Eye, color: "text-orange-600" },
    { label: "Frequency", value: formatFrequency(metrics.avgFrequency), icon: Radio, color: "text-indigo-600" },
  ];

  return (
    <div className="space-y-4">
      {/* Primary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {primaryKpis.map((kpi) => (
          <Card key={kpi.label}>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <kpi.icon className={`h-3.5 w-3.5 ${kpi.color}`} />
                {kpi.label}
              </div>
              <p className="text-xl font-bold tracking-tight">{kpi.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Secondary Metrics */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Additional Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Impressions</p>
              <p className="font-semibold">{formatNumber(metrics.totalImpressions)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Reach</p>
              <p className="font-semibold">{formatNumber(metrics.totalReach)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Clicks</p>
              <p className="font-semibold">{formatNumber(metrics.totalClicks)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Link Clicks</p>
              <p className="font-semibold">{formatNumber(metrics.totalLinkClicks)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Cost / Link Click</p>
              <p className="font-semibold">{metrics.totalLinkClicks > 0 ? formatCurrency(metrics.costPerLinkClick) : "—"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Landing Page Views</p>
              <p className="font-semibold">{formatNumber(metrics.totalLandingPageViews)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Budget Pacing */}
      <BudgetPacingCard snapshots={snapshots} />

      {/* Quality Rankings */}
      {(metrics.qualityRanking || metrics.engagementRateRanking || metrics.conversionRateRanking) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Ad Quality Diagnostics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-around py-2">
              <RankingBadge value={metrics.qualityRanking} label="Quality Ranking" />
              <RankingBadge value={metrics.engagementRateRanking} label="Engagement Rate" />
              <RankingBadge value={metrics.conversionRateRanking} label="Conversion Rate" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fatigue Warning */}
      {metrics.avgFrequency > 3.0 && (
        <div className={`rounded-md border p-3 text-sm ${
          metrics.avgFrequency > 4.0
            ? "bg-red-50 border-red-200 text-red-800"
            : "bg-yellow-50 border-yellow-200 text-yellow-800"
        }`}>
          <strong>Frequency {metrics.avgFrequency.toFixed(1)}</strong> — {
            metrics.avgFrequency > 4.0
              ? "High ad fatigue detected. Refresh your creative urgently."
              : "Audience seeing ads frequently. Consider refreshing creative soon."
          }
        </div>
      )}
    </div>
  );
}

// ─── Campaign Drill-Down Types ────────────────────────────

interface DrillDownRow {
  id: string;
  name: string;
  level: "campaign" | "adset" | "ad";
  status: string | null;
  metaId: string;
  parentMetaId?: string;
  metrics: AggregatedMetrics;
}

// ─── Campaigns Tab (with drill-down) ──────────────────────

function CampaignsTab({
  dateFrom,
  dateTo,
  systemLeadsOverlay,
}: {
  dateFrom: string;
  dateTo: string;
  systemLeadsOverlay?: SystemLeadsOverlay;
}) {
  const campaigns = useAdCampaigns();
  const adSets = useAdSets();
  const adCreatives = useAdCreatives();
  const { data: rawSnapshots = [], isLoading } = useAdPerformanceAllLevels({ dateFrom, dateTo });

  // Overlay system leads onto Meta pixel data
  const allSnapshots = useMemo(
    () => systemLeadsOverlay ? overlaySystemLeadsOnSnapshots(rawSnapshots, systemLeadsOverlay) : rawSnapshots,
    [rawSnapshots, systemLeadsOverlay]
  );
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(new Set());
  const [expandedAdSets, setExpandedAdSets] = useState<Set<string>>(new Set());

  const toggleCampaign = (metaId: string) => {
    setExpandedCampaigns((prev) => {
      const next = new Set(prev);
      if (next.has(metaId)) next.delete(metaId);
      else next.add(metaId);
      return next;
    });
  };

  const toggleAdSet = (metaId: string) => {
    setExpandedAdSets((prev) => {
      const next = new Set(prev);
      if (next.has(metaId)) next.delete(metaId);
      else next.add(metaId);
      return next;
    });
  };

  // Build hierarchical data
  const { campaignRows, adsetMap, adMap } = useMemo(() => {
    // Group snapshots by level+id
    const campaignSnaps = new Map<string, AdPerformanceSnapshot[]>();
    const adsetSnaps = new Map<string, AdPerformanceSnapshot[]>();
    const adSnaps = new Map<string, AdPerformanceSnapshot[]>();

    for (const snap of allSnapshots) {
      if (snap.level === "campaign" && snap.meta_campaign_id) {
        const arr = campaignSnaps.get(snap.meta_campaign_id) ?? [];
        arr.push(snap);
        campaignSnaps.set(snap.meta_campaign_id, arr);
      } else if (snap.level === "adset" && snap.meta_adset_id) {
        const arr = adsetSnaps.get(snap.meta_adset_id) ?? [];
        arr.push(snap);
        adsetSnaps.set(snap.meta_adset_id, arr);
      } else if (snap.level === "ad" && snap.meta_ad_id) {
        const arr = adSnaps.get(snap.meta_ad_id) ?? [];
        arr.push(snap);
        adSnaps.set(snap.meta_ad_id, arr);
      }
    }

    // Build campaign rows
    const cRows: DrillDownRow[] = [];
    for (const [metaId, snaps] of campaignSnaps) {
      const campaign = campaigns.data?.find((c) => c.meta_campaign_id === metaId);
      cRows.push({
        id: metaId,
        name: campaign?.meta_campaign_name ?? metaId,
        level: "campaign",
        status: campaign?.status ?? null,
        metaId,
        metrics: aggregateSnapshots(snaps),
      });
    }
    cRows.sort((a, b) => b.metrics.totalSpend - a.metrics.totalSpend);

    // Build adset lookup: metaCampaignId → adset rows
    const asMap = new Map<string, DrillDownRow[]>();
    // We need campaign→adset mapping. Ad sets have campaign_id (internal), campaigns have meta_campaign_id.
    // Build internal→meta mapping
    const internalToMetaCampaign = new Map<string, string>();
    for (const c of campaigns.data ?? []) {
      internalToMetaCampaign.set(c.id, c.meta_campaign_id);
    }

    for (const as of adSets.data ?? []) {
      const metaCampaignId = internalToMetaCampaign.get(as.campaign_id);
      if (!metaCampaignId) continue;
      const snaps = adsetSnaps.get(as.meta_adset_id) ?? [];
      const row: DrillDownRow = {
        id: as.meta_adset_id,
        name: as.meta_adset_name,
        level: "adset",
        status: as.status,
        metaId: as.meta_adset_id,
        parentMetaId: metaCampaignId,
        metrics: aggregateSnapshots(snaps),
      };
      const existing = asMap.get(metaCampaignId) ?? [];
      existing.push(row);
      asMap.set(metaCampaignId, existing);
    }
    // Sort each group by spend
    for (const rows of asMap.values()) {
      rows.sort((a, b) => b.metrics.totalSpend - a.metrics.totalSpend);
    }

    // Build ad lookup: metaAdsetId → ad rows
    const aMap = new Map<string, DrillDownRow[]>();
    // Build internal→meta adset mapping
    const internalToMetaAdset = new Map<string, string>();
    for (const as of adSets.data ?? []) {
      internalToMetaAdset.set(as.id, as.meta_adset_id);
    }

    for (const ad of adCreatives.data ?? []) {
      const metaAdsetId = internalToMetaAdset.get(ad.adset_id);
      if (!metaAdsetId) continue;
      const snaps = adSnaps.get(ad.meta_ad_id) ?? [];
      const row: DrillDownRow = {
        id: ad.meta_ad_id,
        name: ad.meta_ad_name,
        level: "ad",
        status: ad.status,
        metaId: ad.meta_ad_id,
        parentMetaId: metaAdsetId,
        metrics: aggregateSnapshots(snaps),
      };
      const existing = aMap.get(metaAdsetId) ?? [];
      existing.push(row);
      aMap.set(metaAdsetId, existing);
    }
    for (const rows of aMap.values()) {
      rows.sort((a, b) => b.metrics.totalSpend - a.metrics.totalSpend);
    }

    return { campaignRows: cRows, adsetMap: asMap, adMap: aMap };
  }, [allSnapshots, campaigns.data, adSets.data, adCreatives.data]);

  if (isLoading || campaigns.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (campaignRows.length === 0) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        <p>No campaigns found. Sync your Meta Ads account first.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[280px]">Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Spend</TableHead>
            <TableHead className="text-right">Leads</TableHead>
            <TableHead className="text-right">CPL</TableHead>
            <TableHead className="text-right">Impr.</TableHead>
            <TableHead className="text-right">Clicks</TableHead>
            <TableHead className="text-right">CTR</TableHead>
            <TableHead className="text-right">CPC</TableHead>
            <TableHead className="text-right">CPM</TableHead>
            <TableHead className="text-right">Freq.</TableHead>
            <TableHead className="text-right">Reach</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {campaignRows.map((campaign) => {
            const isExpanded = expandedCampaigns.has(campaign.metaId);
            const childAdSets = adsetMap.get(campaign.metaId) ?? [];
            const hasChildren = childAdSets.length > 0;

            return (
              <Fragment key={campaign.metaId}>
                {/* Campaign Row */}
                <DrillDownTableRow
                  row={campaign}
                  depth={0}
                  isExpanded={isExpanded}
                  hasChildren={hasChildren}
                  onToggle={() => toggleCampaign(campaign.metaId)}
                />

                {/* Ad Set Rows */}
                {isExpanded &&
                  childAdSets.map((adset) => {
                    const isAdsetExpanded = expandedAdSets.has(adset.metaId);
                    const childAds = adMap.get(adset.metaId) ?? [];
                    const hasAdChildren = childAds.length > 0;

                    return (
                      <Fragment key={adset.metaId}>
                        <DrillDownTableRow
                          row={adset}
                          depth={1}
                          isExpanded={isAdsetExpanded}
                          hasChildren={hasAdChildren}
                          onToggle={() => toggleAdSet(adset.metaId)}
                        />

                        {/* Ad Rows */}
                        {isAdsetExpanded &&
                          childAds.map((ad) => (
                            <DrillDownTableRow
                              key={ad.metaId}
                              row={ad}
                              depth={2}
                              isExpanded={false}
                              hasChildren={false}
                              onToggle={() => {}}
                            />
                          ))}
                      </Fragment>
                    );
                  })}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/** Reusable row for the drill-down table */
function DrillDownTableRow({
  row,
  depth,
  isExpanded,
  hasChildren,
  onToggle,
}: {
  row: DrillDownRow;
  depth: number;
  isExpanded: boolean;
  hasChildren: boolean;
  onToggle: () => void;
}) {
  const m = row.metrics;
  const indent = depth * 24;

  const levelIcon =
    row.level === "campaign" ? (
      <Layers className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
    ) : row.level === "adset" ? (
      <MonitorSmartphone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
    ) : (
      <MousePointerClick className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
    );

  return (
    <TableRow className={depth > 0 ? "bg-muted/30" : ""}>
      <TableCell>
        <div className="flex items-center gap-1.5" style={{ paddingLeft: indent }}>
          {hasChildren ? (
            <button onClick={onToggle} className="p-0.5 hover:bg-muted rounded shrink-0">
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </button>
          ) : (
            <span className="w-[18px]" />
          )}
          {levelIcon}
          <span className="font-medium truncate max-w-[200px] text-sm">{row.name}</span>
        </div>
      </TableCell>
      <TableCell>
        <StatusBadge status={row.status} />
      </TableCell>
      <TableCell className="text-right font-mono text-sm">{formatCurrency(m.totalSpend)}</TableCell>
      <TableCell className="text-right text-sm">{formatNumber(m.totalLeads)}</TableCell>
      <TableCell className="text-right font-mono text-sm">
        {m.totalLeads > 0 ? formatCurrency(m.avgCPL) : "—"}
      </TableCell>
      <TableCell className="text-right text-sm">{formatNumber(m.totalImpressions)}</TableCell>
      <TableCell className="text-right text-sm">{formatNumber(m.totalClicks)}</TableCell>
      <TableCell className="text-right text-sm">{formatPct(m.avgCTR)}</TableCell>
      <TableCell className="text-right font-mono text-sm">
        {m.totalClicks > 0 ? formatCurrency(m.avgCPC) : "—"}
      </TableCell>
      <TableCell className="text-right font-mono text-sm">
        {m.totalImpressions > 0 ? formatCurrency(m.avgCPM) : "—"}
      </TableCell>
      <TableCell className="text-right text-sm">
        {m.avgFrequency > 0 ? formatFrequency(m.avgFrequency) : "—"}
      </TableCell>
      <TableCell className="text-right text-sm">{formatNumber(m.totalReach)}</TableCell>
    </TableRow>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <Badge
      variant="outline"
      className={
        status === "ACTIVE"
          ? "border-green-500 text-green-700"
          : status === "PAUSED"
          ? "border-yellow-500 text-yellow-700"
          : ""
      }
    >
      {status}
    </Badge>
  );
}

// ─── Trends Tab ───────────────────────────────────────────

function TrendsTab({
  snapshots,
  isLoading,
}: {
  snapshots: AdPerformanceSnapshot[];
  isLoading: boolean;
}) {
  const dailyData = useMemo(() => {
    const map = new Map<string, { spend: number; leads: number; clicks: number; impressions: number; reach: number }>();
    for (const snap of snapshots) {
      const existing = map.get(snap.date);
      if (existing) {
        existing.spend += snap.spend || 0;
        existing.leads += snap.leads_count || 0;
        existing.clicks += snap.clicks || 0;
        existing.impressions += snap.impressions || 0;
        existing.reach += snap.reach || 0;
      } else {
        map.set(snap.date, {
          spend: snap.spend || 0,
          leads: snap.leads_count || 0,
          clicks: snap.clicks || 0,
          impressions: snap.impressions || 0,
          reach: snap.reach || 0,
        });
      }
    }
    return Array.from(map.entries())
      .map(([date, data]) => ({
        date,
        dateLabel: formatDate(date),
        spend: Math.round(data.spend * 100) / 100,
        leads: data.leads,
        cpl: data.leads > 0 ? Math.round((data.spend / data.leads) * 100) / 100 : 0,
        cpm: data.impressions > 0 ? Math.round((data.spend / data.impressions) * 1000 * 100) / 100 : 0,
        frequency: data.reach > 0 ? Math.round((data.impressions / data.reach) * 10) / 10 : 0,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [snapshots]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (dailyData.length === 0) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        <p>No trend data available. Sync your Meta Ads account first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Spend vs Leads</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="spend" tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
              <YAxis yAxisId="leads" orientation="right" tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value: number, name: string) =>
                  name === "Spend" ? formatCurrency(value) : formatNumber(value)
                }
              />
              <Legend />
              <Area yAxisId="spend" type="monotone" dataKey="spend" stroke="#1a1a2e" fill="#1a1a2e" fillOpacity={0.1} name="Spend" />
              <Area yAxisId="leads" type="monotone" dataKey="leads" stroke="#D4A843" fill="#D4A843" fillOpacity={0.15} name="Leads" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Cost Per Lead Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={dailyData.filter((d) => d.cpl > 0)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
              <Area type="monotone" dataKey="cpl" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.1} name="CPL" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">CPM Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={dailyData.filter((d) => d.cpm > 0)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
              <Area type="monotone" dataKey="cpm" stroke="#ea580c" fill="#ea580c" fillOpacity={0.1} name="CPM" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Frequency Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={dailyData.filter((d) => d.frequency > 0)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value: number) => formatFrequency(value)} />
              <Area type="monotone" dataKey="frequency" stroke="#4f46e5" fill="#4f46e5" fillOpacity={0.1} name="Frequency" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────

export default function AdminMediaBuying() {
  const { toast } = useToast();
  const [dateRange, setDateRange] = useState<DateRangeValue | undefined>(undefined);
  const [datePreset, setDatePreset] = useState<PresetKey | undefined>("30d");
  const triggerSync = useTriggerMetaSync();

  const { from: dateFrom, to: dateTo } = useMemo(
    () => getDateRange(datePreset, dateRange),
    [datePreset, dateRange]
  );

  const {
    data: rawCampaignSnapshots = [],
    isLoading: snapshotsLoading,
  } = useAdPerformanceSnapshots({
    dateFrom,
    dateTo,
    level: "campaign",
  });

  // System leads: fetch attribution data and build overlay maps
  const campaigns = useAdCampaigns();
  const adSets = useAdSets();
  const adCreatives = useAdCreatives();
  const { attributionData: systemLeadsData, attributionAvailable: _attributionAvailable } = useSystemLeadsAttribution(dateFrom, dateTo);

  const systemLeadsOverlay = useMemo(
    () => buildSystemLeadsOverlay(
      systemLeadsData,
      adCreatives.data ?? [],
      campaigns.data ?? [],
      adSets.data ?? []
    ),
    [systemLeadsData, adCreatives.data, campaigns.data, adSets.data]
  );

  // Patch campaign snapshots with system leads for Overview and Trends tabs
  const campaignSnapshots = useMemo(
    () => systemLeadsOverlay.loaded
      ? overlaySystemLeadsOnSnapshots(rawCampaignSnapshots, systemLeadsOverlay)
      : rawCampaignSnapshots,
    [rawCampaignSnapshots, systemLeadsOverlay]
  );

  const handleSync = async () => {
    try {
      await triggerSync.mutateAsync();
      toast({ title: "Sync triggered", description: "Meta Ads data sync started." });
    } catch {
      toast({
        title: "Sync failed",
        description: "Could not trigger Meta Ads sync. Check Edge Function logs.",
        variant: "destructive",
      });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Media Buying</h1>
            <p className="text-sm text-muted-foreground">
              Meta Ads performance — TJ Command Center
            </p>
          </div>
          <div className="flex items-center gap-2">
            <DateRangeFilter
              value={dateRange}
              onChange={setDateRange}
              activePreset={datePreset}
              onPresetChange={setDatePreset}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleSync}
              disabled={triggerSync.isPending}
            >
              {triggerSync.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Sync
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
            <TabsTrigger value="creatives">Creatives</TabsTrigger>
            <TabsTrigger value="geo">By State</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
            <TabsTrigger value="health">Account Health</TabsTrigger>
            <TabsTrigger value="advisor" className="gap-1">
              <Brain className="h-3.5 w-3.5" />
              AI Advisor
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab snapshots={campaignSnapshots} isLoading={snapshotsLoading} />
          </TabsContent>

          <TabsContent value="campaigns">
            <CampaignsTab dateFrom={dateFrom} dateTo={dateTo} systemLeadsOverlay={systemLeadsOverlay} />
          </TabsContent>

          <TabsContent value="creatives">
            <CreativesTab dateFrom={dateFrom} dateTo={dateTo} systemLeadsOverlay={systemLeadsOverlay} />
          </TabsContent>

          <TabsContent value="geo">
            <GeoPerformanceTab dateFrom={dateFrom} dateTo={dateTo} systemLeadsOverlay={systemLeadsOverlay} />
          </TabsContent>

          <TabsContent value="trends">
            <TrendsTab snapshots={campaignSnapshots} isLoading={snapshotsLoading} />
          </TabsContent>

          <TabsContent value="health">
            <AccountHealthTab />
          </TabsContent>

          <TabsContent value="advisor">
            <AIAdvisorTab />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
